/**
 * printable-masks/index.js — the page: choose a character, see it on paper,
 * colour it with a finger, print a set for a whole group.
 *
 * Everything drawn here goes through compose.js, the same paint list the PDF
 * prints; the preview IS the paper, scaled.
 */
import { PRESETS, THEMES, TALES, SLOTS, CRAYONS, holidaysFor } from './catalog.js';
import { build, paint, toSvg, sizeMm, scaleFor, resolve, AGES } from './compose.js';
import { bandStrips, planSheets, CAPTION, PAPERS, GAP, MARGIN, FOOT } from './sheet.js';
import { buildPdf, ensurePdfLib } from './pdf.js';

const BAND_PLATE = 0.85;

const SLUG = 'printable-masks';
const CFG = window.__MASKS__ || {};
const T = CFG.t || {};
const t = (k, fb) => (T[k] != null ? T[k] : (fb != null ? fb : k));
const $ = id => document.getElementById(id);
const LOCALE = window.__LOCALE || 'en';

const state = {
    theme: 'forest', tale: 'teremok', preset: 'hare', custom: null,
    format: 'face', age: '5-7', head: null, cut: 'detail', paper: 'a4', mode: 'color', eco: false,
    paint: false, crayon: CRAYONS[0], overrides: {}, colors: {}, alt: {},
    slot: 'ears', which: 'this', count: 1,
};

const nameOf = id => id === 'custom' ? t('custom_name', 'My character') : t('m_' + id, id);
const charKey = () => state.custom ? 'custom' : state.preset;

/** The character as drawn now: slots + palette (alt, colour pickers). */
function charOf(id) {
    const base = id === 'custom' ? state.custom : resolve(id);
    if (!base) return resolve('hare');
    const c = JSON.parse(JSON.stringify(base));
    const src = id === 'custom' ? null : PRESETS[id];
    const ai = state.alt[id] || 0;
    if (src && src.alts && ai > 0 && src.alts[ai - 1]) Object.assign(c.colors, src.alts[ai - 1]);
    Object.assign(c.colors, state.colors[id] || {});
    return c;
}

function paintOpts(id, extra) {
    return Object.assign({
        mode: state.mode === 'color' ? 'color' : 'line',
        cut: state.cut, eco: state.eco,
        overrides: state.overrides[id] || {},
        scale: scaleFor(state.age),
    }, extra || {});
}

function headCm() {
    const v = parseFloat(String(state.head == null ? '' : state.head).replace(',', '.'));
    return isFinite(v) && v >= 40 && v <= 66 ? v : AGES[state.age].head;
}

/* ── preview ─────────────────────────────────────────────────────────── */

let firstRender = true;
function render() {
    const id = charKey();
    const ch = charOf(id);
    const painted = state.format === 'band' ? bandPlate(id).painted : paint(build(ch, { format: state.format }), paintOpts(id));
    const paper = $('pmPaper');
    paper.innerHTML = toSvg(painted, { keys: true, label: nameOf(id), pad: 2 / painted.scale });
    paper.classList.toggle('paint', state.paint);
    if (state.mode === 'sample') {
        const mini = paint(build(ch, { format: state.format }), paintOpts(id, { mode: 'color', scale: 1 }));
        paper.insertAdjacentHTML('beforeend', '<div style="position:absolute;right:10px;bottom:10px;width:22%;max-width:110px;padding:4px;border:1px dashed #c7ccd4;border-radius:8px;background:#fff">'
            + toSvg(mini, { width: '100%', height: 70 }) + '<div style="font-size:12px;text-align:center;color:#555">' + t('sample_caption', 'sample') + '</div></div>');
    }
    const sz = sizeMm(painted);
    const job = jobPieces();
    const pages = planSheets(job.pieces, state.paper);
    const shrunk = pages.some(p => p.placed.some(q => q.f < 0.999));
    const land = pages.some(p => p.land);
    $('pmCap').innerHTML = '<span><b>' + esc(nameOf(id)) + '</b></span><span>' + esc(t('size_line', ':w × :h cm')
        .replace(':w', fmt1(sz.w / 10)).replace(':h', fmt1(sz.h / 10))) + '</span><span>'
        + esc(t('sheets_line', 'Sheets: :n').replace(':n', pages.length)) + '</span><span>'
        + esc(t('masks_line', 'Masks: :n').replace(':n', job.count)) + '</span>';
    const warn = $('pmWarn');
    const msg = shrunk ? t('warn_shrunk', '') : (land ? t('warn_landscape', '') : '');
    warn.textContent = msg; warn.hidden = !msg;
    $('pmSumChar').textContent = nameOf(id);
    $('pmSumView').textContent = [t('fmt_' + state.format), t('age_' + state.age.replace('-', '')), t('cut_' + state.cut)].join(' · ');
    $('pmSumSet').textContent = t('masks_line', 'Masks: :n').replace(':n', job.count);
    $('pmFmtHint').textContent = t('fmt_hint_' + state.format, '');
    $('pmHeadRow').hidden = state.format !== 'band';
    if (state.format === 'band' && document.activeElement !== $('pmHead')) $('pmHead').value = headCm();
    if (firstRender) { firstRender = false; try { if (window.__stage) window.__stage('built'); } catch (e) { } }
}

const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const fmt1 = v => (Math.round(v * 10) / 10).toLocaleString((window.__intlLocale && window.__intlLocale()) || undefined);

/* ── gallery: holidays, themes, tales, characters ────────────────────── */

const thumbCache = {};
function thumb(id) {
    if (!thumbCache[id]) {
        const p = paint(build(resolve(id), { format: 'face' }), { mode: 'color', cut: 'detail', scale: 1 });
        thumbCache[id] = toSvg(p, {});
    }
    return thumbCache[id];
}

function themeItems() {
    if (state.theme === 'tales') return (TALES.find(x => x.id === state.tale) || TALES[0]).items;
    return (THEMES.find(x => x.id === state.theme) || THEMES[0]).items;
}

function renderThemes() {
    $('pmThemes').innerHTML = THEMES.map(th => '<button type="button" role="tab" class="pm-theme' + (th.id === state.theme ? ' on' : '')
        + '" aria-selected="' + (th.id === state.theme) + '" data-pmtheme="' + th.id + '"><span aria-hidden="true">' + th.icon + '</span>'
        + esc(t('theme_' + th.id)) + '</button>').join('');
    const tales = $('pmTales');
    tales.hidden = state.theme !== 'tales';
    if (state.theme === 'tales') {
        tales.innerHTML = TALES.map(tl => '<button type="button" class="pm-theme' + (tl.id === state.tale ? ' on' : '') + '" data-pmtale="' + tl.id + '">'
            + esc(t('tale_' + tl.id)) + '</button>').join('');
    }
    $('pmWhichTheme').textContent = state.theme === 'tales' ? t('set_tale') : t('set_theme');
    renderGallery();
}

function renderGallery() {
    const cur = state.custom ? null : state.preset;
    $('pmGal').innerHTML = themeItems().map(id => '<button type="button" class="pm-card' + (id === cur ? ' on' : '') + '" data-pmchar="' + id
        + '" aria-pressed="' + (id === cur) + '">' + thumb(id) + '<span>' + esc(nameOf(id)) + '</span></button>').join('');
}

function renderHolidays() {
    const hs = holidaysFor(LOCALE);
    const box = $('pmHol');
    if (!hs.length) { box.hidden = true; return null; }
    box.innerHTML = hs.map((h, i) => '<button type="button" class="pm-holb" data-pmhol="' + i + '">'
        + esc(t('hol_soon', ':name').replace(':name', t('hol_' + h.id, h.id))) + '</button>').join('');
    box.hidden = false;
    box._hs = hs;
    return hs[0];
}

function applyHoliday(h) {
    state.theme = h.theme;
    const items = (THEMES.find(x => x.id === h.theme) || THEMES[0]).items || [];
    state.preset = h.preset && PRESETS[h.preset] ? h.preset : items[0];
    state.custom = null;
    if (h.format) setFormat(h.format, false);
}

/* ── constructor ─────────────────────────────────────────────────────── */

function slotValue(c, slot) { return slot === 'marks' ? (c.marks.filter(m => m !== 'blush')[0] || (c.marks.includes('blush') ? 'blush' : 'none')) : c[slot]; }

function withSlot(c, slot, v) {
    const n = JSON.parse(JSON.stringify(c));
    if (slot === 'marks') n.marks = v === 'none' ? [] : (v === 'blush' ? ['blush'] : [v].concat(c.marks.includes('blush') ? ['blush'] : []));
    else n[slot] = v;
    return n;
}

function renderSlots() {
    $('pmSlots').innerHTML = SLOTS.map(s => '<button type="button" role="tab" class="tc-seg-b' + (s.id === state.slot ? ' on' : '')
        + '" aria-selected="' + (s.id === state.slot) + '" data-pmslot="' + s.id + '">' + esc(t('slot_' + s.id)) + '</button>').join('');
    renderOpts();
}

function renderOpts() {
    const c = charOf(charKey());
    const slot = SLOTS.find(s => s.id === state.slot) || SLOTS[0];
    const cur = slotValue(c, slot.id);
    $('pmOpts').innerHTML = slot.options.map(o => {
        const p = paint(build(withSlot(c, slot.id, o), { format: 'face' }), { mode: 'color', cut: 'detail', scale: 1 });
        return '<button type="button" class="pm-opt' + (o === cur ? ' on' : '') + '" data-pmopt="' + o + '" aria-pressed="' + (o === cur) + '" aria-label="' + esc(t('slot_' + slot.id)) + ' ' + (slot.options.indexOf(o) + 1) + '">' + toSvg(p, {}) + '</button>';
    }).join('');
    const src = state.custom ? null : PRESETS[state.preset];
    const alts = $('pmAlts');
    if (src && src.alts) {
        const list = [src.colors].concat(src.alts);
        const ai = state.alt[state.preset] || 0;
        alts.innerHTML = list.map((pal, i) => '<button type="button" class="pm-alt' + (i === ai ? ' on' : '') + '" data-pmalt="' + i + '" style="background:' + (pal.base || '#fff') + '" aria-label="' + esc(t('color_base')) + ' ' + (i + 1) + '"></button>').join('');
        alts.hidden = false;
    } else alts.hidden = true;
    $('pmColBase').value = c.colors.base || '#ffffff';
    $('pmColLight').value = c.colors.light || '#ffffff';
    $('pmColAccent').value = c.colors.accent || c.colors.mark || '#ffcf33';
    $('pmSumEdit').textContent = state.custom ? t('custom_name') : '';
}

function makeCustom(fn) {
    const id = charKey();
    const c = charOf(id);
    const next = fn(c);
    if (id !== 'custom') {
        state.overrides.custom = Object.assign({}, state.overrides[id] || {});
        state.colors.custom = {};
    }
    state.custom = next;
    renderGallery(); renderOpts(); render();
}

const pick = a => a[Math.floor(Math.random() * a.length)];
function randomChar() {
    const by = id => (SLOTS.find(s => s.id === id) || {}).options || ['none'];
    const c = resolve('monster1');
    c.head = pick(['round', 'blob', 'square', 'wide', 'egg', 'pear']);
    c.ears = Math.random() < 0.5 ? 'none' : pick(by('ears'));
    c.top = pick(['conehorns', 'unihorn', 'antennae', 'hair', 'goathorns', 'kidhorns', 'none', 'crown']);
    c.back = Math.random() < 0.7 ? 'none' : pick(['spikes', 'plates', 'batwings']);
    c.eyes = pick(['plain', 'brows', 'lashes', 'third', 'spider', 'almond']);
    c.holes = pick(['ell', 'round']);
    c.muzzle = Math.random() < 0.6 ? 'none' : pick(['dino', 'pig', 'batnose', 'cat']);
    c.mouth = pick(['teeth', 'fangs', 'open', 'smile', 'dino']);
    c.marks = [pick(['monsterspots', 'none', 'stripes', 'dots'])].filter(m => m !== 'none');
    if (Math.random() < 0.5) c.marks.push('blush');
    const col = pick(CRAYONS.slice(0, 10));
    c.colors = { base: col, mark: pick(CRAYONS.slice(0, 10)), accent: pick(['#ffe066', '#f7e8a4', '#ffd166', '#ffffff']),
        accent2: pick(CRAYONS.slice(0, 9)), dark: '#2b2b3b', inner: '#ff8fa3', light: '#ffffff', rim: '#2b2b3b' };
    return c;
}

/* ── colouring with a finger ─────────────────────────────────────────── */

function renderCrayons() {
    const box = $('pmCrayons');
    box.innerHTML = CRAYONS.map(c => '<button type="button" class="pm-cr' + (c === state.crayon ? ' on' : '') + '" data-pmcr="' + c + '" style="background:' + c + '" aria-label="' + c + '"></button>').join('')
        + '<button type="button" class="tc-btn ghost' + (state.crayon === 'erase' ? ' on' : '') + '" data-pmcr="erase">' + esc(t('paint_erase')) + '</button>'
        + '<button type="button" class="tc-btn ghost" id="pmPaintReset">' + esc(t('paint_reset')) + '</button>';
}

/* ── the job: which masks, how many, for whom ────────────────────────── */

function names() {
    return ($('pmNames').value || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean).slice(0, 60);
}

function jobEntries() {
    const ns = names();
    const id = charKey();
    if (state.which === 'theme') {
        const list = themeItems();
        const n = ns.length || list.length;
        return Array.from({ length: n }, (_, i) => ({ id: list[i % list.length], name: ns[i] || '' }));
    }
    const n = ns.length || Math.max(1, Math.min(60, parseInt($('pmCount').value, 10) || 1));
    return Array.from({ length: n }, (_, i) => ({ id, name: ns[i] || '' }));
}

/** Headband plate + its strips. The plate rides above the forehead and has no
 *  eye holes, so its size is free: 85 % of the face, and smaller still when
 *  the plate and all its strips would not share one sheet (a hare's ears) —
 *  one child's pieces scattered over two pages get mixed up in a class set. */
function bandPlate(id) {
    const model = build(charOf(id), { format: 'band' });
    let k = scaleFor(state.age) * BAND_PLATE;
    let painted = paint(model, paintOpts(id, { scale: k }));
    let sz = sizeMm(painted);
    let st = bandStrips(headCm(), sz.w, state.paper);
    const room = (PAPERS[state.paper] || PAPERS.a4).h - 2 * MARGIN - FOOT - CAPTION - st.n * (st.h + GAP);
    if (sz.h > room && room > 60) {
        k *= room / sz.h * 0.97;
        painted = paint(model, paintOpts(id, { scale: k }));
        sz = sizeMm(painted);
        st = bandStrips(headCm(), sz.w, state.paper);
    }
    return { painted, sz, st };
}

function jobPieces() {
    const entries = jobEntries();
    const pieces = [];
    const band = state.format === 'band';
    for (const e of entries) {
        const ch = charOf(e.id);
        const cap = nameOf(e.id) + (e.name ? ' — ' + e.name : '');
        let painted, sz, st = null;
        if (band) ({ painted, sz, st } = bandPlate(e.id));
        else { painted = paint(build(ch, { format: state.format }), paintOpts(e.id)); sz = sizeMm(painted); }
        const art = { kind: 'art', painted, caption: cap, w: sz.w, h: sz.h + CAPTION, charId: e.id };
        pieces.push(art);
        if (state.mode === 'sample') {
            const sp0 = paint(build(ch, { format: state.format }), paintOpts(e.id, { mode: 'color', scale: 1 }));
            const s0 = sizeMm(sp0);
            const ks = Math.min(48 / s0.w, 48 / s0.h);
            const sp = paint(build(ch, { format: state.format }), paintOpts(e.id, { mode: 'color', scale: ks }));
            const ss = sizeMm(sp);
            pieces.push({ kind: 'art', painted: sp, caption: t('sample_caption', 'sample'), w: ss.w, h: ss.h + CAPTION });
        }
        if (band) {
            const fill = state.mode === 'color' ? ((ch.colors.base) || '#ffffff') : '#ffffff';
            art.groupH = art.h + st.n * (st.h + GAP);
            for (let i = 0; i < st.n; i++) {
                pieces.push({ kind: 'strip', strip: st, index: i, fill: state.eco ? '#ffffff' : fill, w: st.len, h: st.h,
                    label: i === (st.n > 1 ? 1 : 0) ? e.name : '', glueText: t('pdf_glue', 'glue') });
            }
        }
    }
    return { pieces, count: entries.length, entries };
}

/* ── output ──────────────────────────────────────────────────────────── */

function limitKey(entries) {
    const ids = Array.from(new Set(entries.map(e => e.id))).sort();
    return 'set:' + state.format + ':' + ids.join(',') + ':' + entries.length;
}

function outName(ext) {
    const id = charKey();
    const raw = t('file_prefix', 'masks') + '-' + (state.which === 'theme' ? t(state.theme === 'tales' ? 'tale_' + state.tale : 'theme_' + state.theme) : nameOf(id));
    if (window.__outputName) return window.__outputName.join([window.__outputName.sanitize(raw)], ext);
    return raw.replace(/[^\w\-]+/g, '-') + '.' + ext;
}

let busy = false;
async function savePdf() {
    if (busy) return;
    const job = jobPieces();
    const gate = window.__toolLimit && window.__toolLimit.begin(SLUG, limitKey(job.entries));
    if (window.__toolLimit && !gate) return;
    busy = true;
    const btn = $('pmDlPdf'); const was = btn.textContent;
    btn.disabled = true; btn.textContent = t('building', '…');
    try {
        const blob = await buildPdf({
            paper: state.paper, pieces: job.pieces, title: outName('pdf'),
            footer: t('ins_' + state.format, ''), rulerCaption: t('pdf_ruler', '50 mm'), shrunk: t('pdf_shrunk', ':p %'),
        });
        // The file exists from here on. If the build outlived the tap, the
        // download door holds it behind its own «Save» — the button must not
        // keep saying «building» while it waits for that second tap.
        btn.disabled = false; btn.textContent = was;
        const ok = await window.__downloadFile(blob, outName('pdf'));
        if (gate) { if (ok !== false) gate.commit(); else gate.abort(); }
        if (window.__aeToolComplete) window.__aeToolComplete({ ok: true, f: 'pdf', fmt: state.format, mode: state.mode, n: job.count, age: state.age, which: state.which });
    } catch (err) {
        if (gate) gate.abort();
        if (window.__handleError) window.__handleError(err, 'masks_pdf', { say: t('err_pdf') });
    } finally {
        busy = false; btn.disabled = false; btn.textContent = was;
    }
}

async function savePng() {
    if (busy) return;
    const id = charKey();
    const gate = window.__toolLimit && window.__toolLimit.begin(SLUG, limitKey([{ id }]));
    if (window.__toolLimit && !gate) return;
    busy = true;
    try {
        const painted = paint(build(charOf(id), { format: state.format }), paintOpts(id));
        const sz = sizeMm(painted);
        const ppm = 11.811;                        // 300 dpi
        const w = Math.round(sz.w * ppm), h = Math.round(sz.h * ppm);
        const svg = toSvg(painted, { width: w, height: h });
        const img = new Image();
        const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
        await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error('svg image failed')); img.src = url; });
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const g = c.getContext('2d');
        if (!g) throw (window.__canvasRefused ? window.__canvasRefused(c) : new Error('no 2d'));
        g.fillStyle = '#fff'; g.fillRect(0, 0, w, h); g.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        const blob = await window.__canvasBlob(c, 'image/png');
        const ok = await window.__downloadFile(blob, outName('png'));
        if (gate) { if (ok !== false) gate.commit(); else gate.abort(); }
        if (window.__aeToolComplete) window.__aeToolComplete({ ok: true, f: 'png', fmt: state.format, mode: state.mode, n: 1 });
    } catch (err) {
        if (gate) gate.abort();
        if (window.__handleError) window.__handleError(err, 'masks_png', { say: t('err_pdf') });
    } finally { busy = false; }
}

/* ── settings ────────────────────────────────────────────────────────── */

const TS = () => window.__toolSettings;
function remember(k, v) { try { if (TS()) TS().save(SLUG, k, v); } catch (e) { } }
function recall(k, d) { try { return TS() ? TS().load(SLUG, k, d) : d; } catch (e) { return d; } }

function segOn(attr, v) {
    document.querySelectorAll('[' + attr + ']').forEach(b => {
        const on = b.getAttribute(attr) === v;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', String(on));
    });
}
function setFormat(v, save = true) { state.format = v; segOn('data-pmfmt', v); if (save) remember('format', v); }

function bind() {
    document.addEventListener('click', e => {
        const el = e.target.closest('[data-pmtheme],[data-pmtale],[data-pmchar],[data-pmhol],[data-pmslot],[data-pmopt],[data-pmalt],[data-pmcr],[data-pmmode],[data-pmfmt],[data-pmage],[data-pmcut],[data-pmwhich],#pmPaintReset,#pmRandom,#pmResetChar,#pmDlPdf,#pmDlPng');
        if (!el || !el.closest('.pm')) return;
        const d = el.dataset;
        if (d.pmtheme) { state.theme = d.pmtheme; renderThemes(); return; }
        if (d.pmtale) { state.tale = d.pmtale; renderThemes(); return; }
        if (d.pmchar) { state.preset = d.pmchar; state.custom = null; renderGallery(); renderOpts(); render(); return; }
        if (d.pmhol != null) { const h = ($('pmHol')._hs || [])[+d.pmhol]; if (h) { applyHoliday(h); renderThemes(); renderOpts(); render(); } return; }
        if (d.pmslot) { state.slot = d.pmslot; renderSlots(); return; }
        if (d.pmopt) { const v = d.pmopt; makeCustom(c => withSlot(c, state.slot, v)); return; }
        if (d.pmalt != null) { state.alt[state.preset] = +d.pmalt; delete state.colors[state.preset]; thumbCache[state.preset] = null; renderOpts(); render(); return; }
        if (d.pmcr) { state.crayon = d.pmcr; renderCrayons(); return; }
        if (d.pmmode) { state.mode = d.pmmode; segOn('data-pmmode', d.pmmode); remember('mode', d.pmmode); render(); return; }
        if (d.pmfmt) { setFormat(d.pmfmt); render(); return; }
        if (d.pmage) { state.age = d.pmage; segOn('data-pmage', d.pmage); remember('age', d.pmage); state.head = null; render(); return; }
        if (d.pmcut) { state.cut = d.pmcut; segOn('data-pmcut', d.pmcut); remember('cut', d.pmcut); render(); return; }
        if (d.pmwhich) { state.which = d.pmwhich; segOn('data-pmwhich', d.pmwhich); $('pmCount').closest('.pm-field').hidden = d.pmwhich === 'theme'; render(); return; }
        if (el.id === 'pmPaintReset') { delete state.overrides[charKey()]; render(); return; }
        if (el.id === 'pmRandom') { const r = randomChar(); state.overrides.custom = {}; state.colors.custom = {}; state.custom = r; renderGallery(); renderOpts(); render(); return; }
        if (el.id === 'pmResetChar') { state.custom = null; delete state.colors[state.preset]; delete state.overrides[state.preset]; state.alt[state.preset] = 0; renderGallery(); renderOpts(); render(); return; }
        if (el.id === 'pmDlPdf') { savePdf(); return; }
        if (el.id === 'pmDlPng') { savePng(); return; }
    });

    // Tap-to-colour on the preview: the path's key is the part (both ears at once).
    $('pmPaper').addEventListener('click', e => {
        if (!state.paint) return;
        const p = e.target.closest('[data-k]');
        if (!p) return;
        const k = p.getAttribute('data-k');
        if (k === 'holes' || k === 'elastic') return;
        const id = charKey();
        const ov = state.overrides[id] = state.overrides[id] || {};
        if (state.crayon === 'erase') delete ov[k]; else ov[k] = state.crayon;
        render();
    });

    $('pmPaint').addEventListener('change', e => {
        state.paint = e.target.checked;
        $('pmCrayons').hidden = !state.paint; $('pmPaintTip').hidden = !state.paint;
        if (state.paint) renderCrayons();
        render();
    });
    const colour = (role) => e => {
        const id = charKey();
        state.colors[id] = Object.assign({}, state.colors[id] || {}, { [role]: e.target.value });
        if (role === 'accent') state.colors[id].mark = e.target.value;
        render();
    };
    $('pmColBase').addEventListener('input', colour('base'));
    $('pmColLight').addEventListener('input', colour('light'));
    $('pmColAccent').addEventListener('input', colour('accent'));
    $('pmColBase').addEventListener('change', () => renderOpts());
    $('pmPaperSel').addEventListener('change', e => { state.paper = e.target.value; remember('paper', state.paper); render(); });
    $('pmEco').addEventListener('change', e => { state.eco = e.target.checked; remember('eco', state.eco ? 1 : 0); render(); });
    $('pmHead').addEventListener('change', e => { state.head = e.target.value; render(); });
    $('pmCount').addEventListener('input', () => render());
    $('pmNames').addEventListener('input', () => render());
}

function init() {
    const saved = k => recall(k, null);
    if (saved('format') && ['face', 'half', 'band', 'stick'].includes(saved('format'))) setFormat(saved('format'), false);
    if (saved('age') && AGES[saved('age')]) { state.age = saved('age'); segOn('data-pmage', state.age); }
    if (saved('cut') && ['simple', 'detail'].includes(saved('cut'))) { state.cut = saved('cut'); segOn('data-pmcut', state.cut); }
    if (saved('mode') && ['color', 'line', 'sample'].includes(saved('mode'))) { state.mode = saved('mode'); segOn('data-pmmode', state.mode); }
    const sp = saved('paper');
    state.paper = sp && PAPERS[sp] ? sp : (/-(US|CA)$/i.test(navigator.language || '') ? 'letter' : 'a4');
    $('pmPaperSel').value = state.paper;
    state.eco = String(saved('eco')) === '1'; $('pmEco').checked = state.eco;

    const hol = renderHolidays();
    if (hol) applyHoliday(hol);
    else state.preset = THEMES[0].items[0];
    renderThemes();
    renderSlots();
    bind();
    render();
    // pdf-lib (~500 KB) and the text face are fetched on the first touch of the
    // tool, not on page load: a visitor who only looks pays nothing, and the
    // one who downloads gets the file inside the browser's tap window (~5 s) —
    // past it the download door has to hold the file for a second tap.
    const warm = () => {
        document.querySelector('.pm').removeEventListener('pointerdown', warm, true);
        ensurePdfLib().catch(() => { });
        try { const TF = window.PdfTextFont; if (TF && TF.face) TF.load().then(() => TF.face('noto')).catch(() => { }); } catch (e) { }
    };
    document.querySelector('.pm').addEventListener('pointerdown', warm, true);
    try { if (window.__stage) window.__stage('ready'); } catch (e) { }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
